<template>
  <div
    class="modal fade fixed-end"
    tabindex="-1"
    role="dialog"
  >
    <div
      class="modal-dialog modal-dialog-vertical dw-md-8 dw-lg-6 dw-xl-5"
      role="document"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="drawer-title">Edit Basic Details</h2>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>

        <div class="modal-body">
          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="control-label">Legal Company Name</label>
              <input v-model="editingClient.company" type="text" class="form-control" />
            </div>
            <div class="col-md-6 mb-3">
              <label class="control-label">Display Name</label>
              <input v-model="editingClient.display_name" type="text" class="form-control" />
            </div>
          </div>

          <div class="mb-3">
            <label class="control-label">Primary Contact</label>
            <div class="input-group">
              <span class="input-group-text">
                <i class="fal fa-fw fa-address-card"></i>
              </span>
              <multiselect
                :show-labels="false"
                v-model="primaryContact"
                :options="contacts"
                label="name"
                track-by="id"
                placeholder=""
                @select="contactSelected"
              >
                <template slot="singleLabel" slot-scope="props">
                  <span class="option__title">{{ props.option.name }}</span>
                  <span class="option__small">{{ props.option.email }}</span>
                </template>
                <template slot="option" slot-scope="props">
                  <div class="option__desc">
                    <span class="option__title">{{ props.option.name }}</span>
                    <span class="option__small">{{ props.option.email }}</span>
                  </div>
                </template>
              </multiselect>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="control-label">Phone</label>
              <input v-model="editingClient.phone" type="text" class="form-control" />
            </div>
            <div class="col-md-6 mb-3">
              <label class="control-label">Website</label>
              <input v-model="editingClient.website" type="text" class="form-control" placeholder="https://" />
            </div>
          </div>

          <h3 class="mt-3">Project Agreement</h3>
          <p class="text-muted">Link to a default or upload a custom agreement.</p>
          <div class="mb-3">
            <label class="control-label">Default Project Agreement</label>
            <multiselect
              :show-labels="false"
              v-model="projectAgreement"
              :options="agreements"
              label="title"
              track-by="id"
              placeholder=""
              @select="agreementSelected"
              @remove="agreementRemoved"
            ></multiselect>
          </div>
          <div class="mb-3">
            <label class="control-label" for="client-agreement-file">Upload Custom PDF File</label>
            <input type="file" id="client-agreement-file" ref="file" class="form-control" accept="application/pdf" @change="handleFileUpload"/>
            <small class="form-text text-muted">Replaces the default agreement when saved.</small>
          </div>

          <h3 class="mt-3">Logo</h3>
          <div class="mb-3">
            <label class="control-label" for="client-logo-file">Upload Client Logo</label>
            <input type="file" id="client-logo-file" ref="logo" class="form-control" accept="image/*" @change="handleLogoUpload"/>
          </div>

          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-default"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <c-button @click="handleSave" class="btn btn-rounded btn-primary">Save</c-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { http, notify } from 'utils'
import { mapActions, mapState } from 'vuex'
import { cloneDeep, pick } from 'lodash'
import Multiselect from 'vue-multiselect'
import { CModal } from '@chameleon/chameleon-vue'
import { helperMethods } from 'vx/modules/clients'

export default {
  extends: CModal,

  props: ['client'],

  components: {
    Multiselect
  },

  data () {
    return {
      editingClient: {},
      primaryContact: null,
      projectAgreement: null,
      file: null,
      logo: null
    }
  },

  mounted () {
    this.getProjectAgreements()

    this.addEventListener('open', () => {
      this.editingClient = cloneDeep(this.client)
      this.primaryContact = this.editingClient.primary_contact || null
      this.projectAgreement = this.editingClient.project_agreement || null
      this.$refs.file.value = ''
      this.$refs.logo.value = ''

      this.getContacts(this.client.id)
    })
  },

  computed: {
    ...mapState('contacts', ['contacts']),
    ...mapState('projectAgreements', ['agreements'])
  },

  methods: {
    ...helperMethods,
    ...mapActions('contacts', ['getContacts']),
    ...mapActions('projectAgreements', { getProjectAgreements: 'getList' }),

    contactSelected (contact) {
      this.editingClient.primary_contact_id = contact.id
    },

    agreementSelected (agreement) {
      this.editingClient.project_agreement_file_id = agreement.id
    },

    agreementRemoved () {
      this.editingClient.project_agreement_file_id = null
    },

    handleFileUpload () {
      this.file = this.$refs.file.files[0]
    },

    handleLogoUpload () {
      this.logo = this.$refs.logo.files[0]
    },

    upload (field, file) {
      const formData = new FormData()
      formData.append(field, file)
      formData.append('_method', 'put') // PUT + multipart form request + php = no

      return http.post('/clients/' + this.client.id, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
    },

    handleSave () {
      const payload = pick(this.editingClient, [
        'primary_contact_id',
        'company',
        'display_name',
        'phone',
        'website',
        'project_agreement_file_id'
      ])

      return this.updateClient({ id: this.client.id, payload })
        .then(() => this.uploadFiles())
        .then(() => {
          notify.success('Client successfully updated.')
          this.close()
        })
        .catch((error) => {
          notify.error('Failed to update client, please try again.')
          return Promise.reject(error)
        })
    },

    // Files are uploaded after the details are saved so a custom agreement
    // takes precedence over the selected default
    async uploadFiles () {
      if (!this.file && !this.logo) {
        return
      }

      if (this.file) {
        await this.upload('pdf', this.file)
        this.getProjectAgreements()
      }

      if (this.logo) {
        await this.upload('logo', this.logo)
      }

      await this.getClient(this.client.id)
    }
  }
}
</script>
